import Keycloak, { type KeycloakProfile } from "keycloak-js"
import { ref } from "vue"
import { serializeError } from "serialize-error"
import { createLogger } from "../logger"
import { keycloakUrl, keycloakRealm, keycloakClientId } from "../config"

const logger = createLogger('Keycloak')

const keycloak = new Keycloak({ url: keycloakUrl, realm: keycloakRealm, clientId: keycloakClientId })

export const token = ref<string | null>(null)
export const userProfile = ref<KeycloakProfile | undefined>(undefined)

/**
 * Refreshes the access token if it expires within 30 s. Called before every GraphQL
 * request (apollo-client.ts) and by the expiry timer below — the timer alone is not
 * enough: a backgrounded mobile tab sleeps through it and then sends an expired token.
 *
 * A failed refresh is NOT a dead session. A network drop at the refresh moment used to
 * trigger keycloak.login(), which then failed too and left the editor on the browser's
 * offline error page with unsaved work lost (GlitchTip BACKEND-51, reproduced on stage).
 * Now the next request simply retries. A session that is really dead (Keycloak rejects
 * the refresh token with 400 invalid_grant) is handled by keycloak-js itself:
 * clearToken() calls login() because we init with onLoad 'login-required'.
 *
 * Logged at `info`, NOT `warn`: the logger bridges warn/error to Sentry (see logger.ts),
 * and an expired SSO session is the normal end of a workday, not a fault (~100
 * GlitchTip events on 2026-09-23 before this was lowered).
 */
export async function refreshTokenIfNeeded(): Promise<void> {
  try {
    await keycloak.updateToken(30)
  } catch (error) {
    logger.info({ error: serializeError(error) }, "token refresh failed; next request retries")
  }
}

keycloak.onTokenExpired = () => { void refreshTokenIfNeeded() }
keycloak.onAuthRefreshSuccess = () => { token.value = keycloak.token ?? null }
keycloak.onAuthSuccess = () => { token.value = keycloak.token ?? null }

async function init(): Promise<boolean> {
  const authenticated = await keycloak.init({
    onLoad: 'login-required',
    pkceMethod: 'S256',
    checkLoginIframe: false,
  })
  token.value = keycloak.token ?? null
  if (authenticated) {
    userProfile.value = await keycloak.loadUserProfile()
  }
  return authenticated
}

function hasRole(role: string): boolean {
  return keycloak.hasRealmRole(role)
}

function accountUrl(): string {
  return keycloak.createAccountUrl()
}

const auth = { keycloak, token, userProfile, hasRole, login: () => keycloak.login(), logout: () => keycloak.logout(), accountUrl, init }
export default auth
